import { useState } from 'react';
import { api } from '../api.js';
import { toast } from './Toasts.jsx';

// T086 — Generic AI-suggest panel. Used by:
//   - Onboarding StepPrefs → suggest target roles + locations
//   - Profile page → same buttons + summary drafter
//
// Operator sees: a button "✨ Suggest <thing>". On click, panel expands
// showing 3-5 suggestions with rationale. Each has a "+ add" pill that
// folds it into the current chip-input value. Operator-driven adoption —
// nothing auto-applies.

export default function AiSuggestPanel({
  label, // visible label on the trigger button, e.g. "Suggest target roles"
  endpoint, // backend POST endpoint
  body, // optional POST body (e.g. residenceHint for locations)
  itemKey, // 'roles' or 'locations' — which key on the response holds the array
  nameKey = 'title', // 'title' (roles) or 'name' (locations)
  existing, // string[] — current chip-input value, to dim already-added
  onAdd, // (str) => void — operator clicked "+ add" on a suggestion
  disabled,
  helperText, // optional hint under the trigger
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');

  const fetchSuggestions = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.post(endpoint, body ?? {});
      setItems(r[itemKey] ?? []);
      setOpen(true);
    } catch (e) {
      const msg = e?.body?.message || e?.message || 'Could not fetch suggestions';
      setError(msg);
      toast(msg, { kind: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const existingLower = new Set((existing ?? []).map((s) => String(s).toLowerCase()));

  // Suggestions not yet folded into the chip-input. The parent's onAdd dedups
  // again on its own state, so this is purely for the count + disabled state.
  const addable = items
    .map((item) => item[nameKey])
    .filter((name) => name && !existingLower.has(String(name).toLowerCase()));

  // "+ Add all" — one click folds every remaining suggestion in. onAdd is a
  // functional state update in the parent, so calling it in a loop accumulates
  // correctly (each call sees the prior one's result).
  const addAll = () => {
    if (addable.length === 0) return;
    for (const name of addable) onAdd?.(name);
    toast(`Added ${addable.length} ${itemKey} — review + save`, { kind: 'success' });
  };

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={open && items.length > 0 ? () => setOpen(false) : fetchSuggestions}
        disabled={disabled || loading}
        className="rounded-md border border-slate-700 px-2.5 py-1 text-[11px] text-slate-300 transition hover:border-brand-600 hover:text-brand-400 disabled:opacity-50"
      >
        {loading ? '✨ Suggesting…' : open ? '× hide suggestions' : `✨ ${label}`}
      </button>
      {helperText && !open && (
        <span className="ml-2 text-xs text-slate-500">{helperText}</span>
      )}
      {error && !open && (
        <p className="mt-1 text-xs text-red-400">⚠ {error}</p>
      )}
      {open && items.length > 0 && (
        <div className="mt-2 space-y-1.5 rounded-lg border border-brand-700 bg-brand-600/5 p-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[10px] uppercase tracking-widest text-brand-400">Suggestions</p>
            <button
              type="button"
              onClick={addAll}
              disabled={addable.length === 0}
              className="shrink-0 rounded border border-brand-600 px-2 py-0.5 text-[10px] font-medium text-brand-300 transition hover:bg-brand-600/10 disabled:cursor-not-allowed disabled:border-emerald-700 disabled:text-emerald-500"
            >
              {addable.length === 0 ? '✓ all added' : `+ Add all (${addable.length})`}
            </button>
          </div>
          {items.map((item, i) => {
            const name = item[nameKey] ?? '';
            const alreadyAdded = existingLower.has(name.toLowerCase());
            return (
              <div key={i} className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-slate-100">{name}</p>
                  {item.rationale && <p className="text-[11px] leading-snug text-slate-400">{item.rationale}</p>}
                </div>
                <button
                  type="button"
                  onClick={() => onAdd?.(name)}
                  disabled={alreadyAdded}
                  className={
                    'shrink-0 rounded border px-2 py-0.5 text-[10px] disabled:cursor-not-allowed ' +
                    (alreadyAdded
                      ? 'border-emerald-700 text-emerald-500 bg-emerald-950/30'
                      : 'border-brand-600 text-brand-400 hover:bg-brand-600/10')
                  }
                >
                  {alreadyAdded ? '✓ added' : '+ add'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
